import { useEffect, useId, useState, type Dispatch, type SetStateAction } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/marketing/components/ui/button';
import { Input } from '@/marketing/components/ui/input';
import { scoutingPageQuery } from '@/marketing/infrastructure/influencer/campaign/scouting/queries';
import type { ScoutingSummary } from '@/marketing/models/campaignScouting';
import type { CampaignInfluencer } from '@/marketing/models/campaignInfluencer';
import { ConfirmScoutingSelectionDialog } from './ConfirmScoutingSelectionDialog';
import { ConfirmScoutingDeclineDialog } from './ConfirmScoutingDeclineDialog';
import { ScoutingTable } from './ScoutingTable';
import { ScoutingFilters } from './ScoutingFilters';
import { ScoutingExports } from './ScoutingExports';
import { ScoutingNotesDialog } from './ScoutingNotesDialog';
import { ScoutingProductDialog } from './ScoutingProductDialog';
import {
  type ScoutingViewRow,
  isSelectableFinalist,
  filterScoutingRows,
  type ScoutingFilter,
  type ScoutingCandidateSnapshot,
} from './scoutingView';
import { useScoutingActions } from './useScoutingActions';

interface ScoutingTabProps {
  campaignId: string;
  pipelineType?: string | undefined;
  onRowClick: (creator: CampaignInfluencer, visibleQueue?: CampaignInfluencer[]) => void;
  onSummary?: (summary: ScoutingSummary) => void;
}
function CampaignScouting({ campaignId, pipelineType, onRowClick, onSummary }: ScoutingTabProps) {
  const panelId = useId();
  const [filter, setFilter] = useState<ScoutingFilter>('All');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [notesTarget, setNotesTarget] = useState<ScoutingViewRow>();
  const [productTarget, setProductTarget] = useState<ScoutingViewRow>();
  const [selected, setSelected] = useState<Map<string, ScoutingCandidateSnapshot>>(new Map());
  const checked = new Set(selected.keys());
  const setChecked: Dispatch<SetStateAction<Set<string>>> = (action) =>
    setSelected((current) => {
      const next = typeof action === 'function' ? action(new Set(current.keys())) : action;
      return new Map([...current].filter(([id]) => next.has(id)));
    });
  const actions = useScoutingActions(campaignId, setChecked);
  const scouting = useQuery(
    scoutingPageQuery(campaignId, filter === 'Finalist' ? { status: 'Finalist' } : {
      limit: 50,
      status: filter,
      search,
      ...(cursors[pageIndex] ? { cursor: cursors[pageIndex] } : {}),
    }),
  );
  const rows: ScoutingViewRow[] = scouting.data?.rows ?? [];
  const visible = filter === 'Finalist' ? filterScoutingRows(rows, filter, search) : rows;
  const supportsBrandSelection = pipelineType === 'performance_seeding' || (!pipelineType && rows.some((row) => row.creator?.pipelineType === 'performance_seeding'));
  const finalists = (filter === 'Finalist' ? rows : visible).filter(isSelectableFinalist);
  const candidates = [...selected.values()];
  const unavailable = scouting.isPending || scouting.isError;
  const busy = actions.pending || scouting.isFetching || searchInput.trim().toLowerCase() !== search;
  const allVisibleChecked = finalists.length > 0 && finalists.every((row) => checked.has(row.influencerId));
  useEffect(() => {
    if (searchInput.trim().toLowerCase() === search) {
      return;
    }
    const timer = setTimeout(() => {
      setSearch(searchInput.trim().toLowerCase());
      setCursors([undefined]);
      setPageIndex(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, search]);
  useEffect(() => {
    if (scouting.data) {
      onSummary?.({ ...scouting.data.summary, rejected: scouting.data.counts.Rejected });
    }
  }, [scouting.data, onSummary]);
  // Only observed rows can invalidate a selection. Off-page selections keep their captured version.
  useEffect(() => {
    if (!scouting.isSuccess || scouting.isFetching) {
      return;
    }
    const ineligible = new Set(
      scouting.data.rows.filter((row) => !isSelectableFinalist(row)).map((row) => row.influencerId),
    );
    if (ineligible.size) {
      setSelected((current) => new Map([...current].filter(([id]) => !ineligible.has(id))));
    }
  }, [scouting.data, scouting.isSuccess, scouting.isFetching]);
  const refresh = () => scouting.refetch();
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        const row = rows.find((candidate) => candidate.influencerId === id);
        if (row && isSelectableFinalist(row)) {
          next.set(id, {
            influencerId: id,
            expectedVersion: row.version,
            ...(row.creator?.handle ? { handle: row.creator.handle } : {}),
          });
        }
      }
      return next;
    });
  const toggleVisible = () =>
    setSelected((current) => {
      const next = new Map(current);
      for (const row of finalists) {
        if (allVisibleChecked) {
          next.delete(row.influencerId);
        } else if (!next.has(row.influencerId)) {
          next.set(row.influencerId, {
            influencerId: row.influencerId,
            expectedVersion: row.version,
            ...(row.creator?.handle ? { handle: row.creator.handle } : {}),
          });
        }
      }
      return next;
    });
  const changeFilter = (value: ScoutingFilter) => {
    setFilter(value);
    setCursors([undefined]);
    setPageIndex(0);
  };
  const nextPage = () => {
    const cursor = scouting.data?.pageInfo.nextCursor;
    if (!cursor || busy) {
      return;
    }
    setCursors((current) => [...current.slice(0, pageIndex + 1), cursor]);
    setPageIndex((current) => current + 1);
  };
  return (
    <section aria-label="Campaign scouting" className="tw-space-y-3 tw-px-8 tw-py-4">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <div>
          <h2 className="tw-text-base tw-font-semibold">Scouting</h2>
          <p className="tw-text-xs tw-text-muted-foreground">
            Review applicants, mark finalists, then confirm who moves to Selected.
          </p>
        </div>
        <div className="tw-flex tw-gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void refresh()}>
            Refresh scouting
          </Button>
          <Button
            size="sm"
            disabled={unavailable || busy || candidates.length === 0}
            onClick={() => actions.openConfirmation(candidates, 'Shortlisted')}
          >
            Selected ({candidates.length})
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!supportsBrandSelection || unavailable || busy || candidates.length === 0}
            title={supportsBrandSelection ? 'Confirm brand approval and move to Brand Shortlisted' : 'Brand selection is available for Performance Seeding campaigns'}
            onClick={() => actions.openConfirmation(candidates, 'Brand Shortlisted')}
          >
            Brand Selected ({candidates.length})
          </Button>
        </div>
      </div>
      <ScoutingFilters value={filter} onChange={changeFilter} panelId={panelId} counts={scouting.data?.counts} />
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <Input
          aria-label="Search scouting"
          placeholder="Search creators…"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          className="tw-h-9 tw-max-w-[260px]"
        />
        <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs">
          <input
            type="checkbox"
            checked={allVisibleChecked}
            disabled={unavailable || busy || finalists.length === 0}
            onChange={toggleVisible}
          />
          {filter === 'Finalist' ? 'Check all finalists' : 'Check visible finalists'}
        </label>
        <span className="tw-text-xs tw-text-muted-foreground">
          {checked.size} checked · Checked finalists stay selected across pages and filters;
          confirmation uses their captured versions.
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={actions.pending || selected.size === 0}
        onClick={() => setSelected(new Map())}
      >
        Clear checked
      </Button>
      <ScoutingExports
        campaignId={campaignId}
        counts={scouting.data?.counts}
        disabled={unavailable || actions.pending}
      />
      <p className="tw-text-[11px] tw-text-muted-foreground">
        Followers and Avg views show stored profile observations. Missing values show a dash; zero stays zero.
        Average-view sample periods are unavailable. Expand Data sources for observation dates and coverage.
      </p>
      {actions.notice && (
        <p role="status" className="tw-text-sm">
          {actions.notice}
        </p>
      )}
      {actions.error && !actions.declineTarget && (
        <p role="alert" className="tw-text-sm tw-text-red-700">
          {actions.error}
        </p>
      )}
      <div role="tabpanel" id={panelId} aria-labelledby={`${panelId}-${filter}`}>
        {scouting.isError ? (
          <div role="alert" className="tw-rounded tw-border tw-border-red-200 tw-p-4 tw-text-sm">
            Could not load scouting. Refresh before making decisions.
            <Button size="sm" variant="outline" className="tw-ml-3" onClick={() => void refresh()}>
              Retry scouting
            </Button>
          </div>
        ) : scouting.isPending ? (
          <p role="status">Loading scouting…</p>
        ) : visible.length === 0 ? (
          <p className="tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground">No creators match this view.</p>
        ) : (
          <ScoutingTable
            rows={visible}
            checked={checked}
            disabled={busy}
            onToggle={toggle}
            onDecide={actions.decide}
            onOpen={onRowClick}
            onProducts={setProductTarget}
            onNotes={setNotesTarget}
            pageIndex={pageIndex}
            totalMatching={filter === 'Finalist' ? visible.length : scouting.data?.pageInfo.totalMatching}
          />
        )}
      </div>
      {filter !== 'Finalist' &&<div className="tw-flex tw-items-center tw-gap-3 tw-text-xs" aria-label="Scouting pagination">
        <Button
          size="sm"
          variant="outline"
          disabled={busy || pageIndex === 0}
          onClick={() => setPageIndex((current) => current - 1)}
        >
          Previous page
        </Button>
        <span>
          Page {pageIndex + 1} · {scouting.data?.pageInfo.totalMatching ?? '…'} matching · 50 per page
        </span>
        <Button size="sm" variant="outline" disabled={busy || !scouting.data?.pageInfo.hasMore} onClick={nextPage}>
          Next page
        </Button>
      </div>}
      {notesTarget && (
        <ScoutingNotesDialog campaignId={campaignId} row={notesTarget} onClose={() => setNotesTarget(undefined)} />
      )}
      {productTarget && (
        <ScoutingProductDialog
          campaignId={campaignId}
          row={productTarget}
          onClose={() => setProductTarget(undefined)}
        />
      )}
      {actions.snapshot && (
        <ConfirmScoutingSelectionDialog
          candidates={actions.snapshot.candidates}
          targetStage={actions.snapshot.targetStage}
          pending={actions.selectionPending}
          onCancel={actions.cancelConfirmation}
          onConfirm={actions.confirm}
        />
      )}
      {actions.declineTarget && (
        <ConfirmScoutingDeclineDialog
          creatorName={
            actions.declineTarget.creator?.handle
              ? `@${actions.declineTarget.creator.handle}`
              : actions.declineTarget.influencerId
          }
          pending={actions.declinePending}
          error={actions.error}
          onCancel={actions.cancelDecline}
          onConfirm={actions.confirmDecline}
        />
      )}
    </section>
  );
}
export function ScoutingTab(props: ScoutingTabProps) {
  return <CampaignScouting key={props.campaignId} {...props} />;
}
